import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  MessageSquare, Crown, Send, Plus, Clock, CheckCircle, XCircle, 
  Phone, User, FileText, Settings, Save, Trash2 
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SMSBlastPage({ user, subscription, onLogout }) {
  const [deals, setDeals] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const [smsForm, setSmsForm] = useState({
    to_numbers: [],
    message: "",
    deal_id: ""
  });

  const [templateForm, setTemplateForm] = useState({
    name: "",
    message: ""
  });

  const [manualNumber, setManualNumber] = useState("");

  const tier = subscription?.tier || "starter";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, logsRes, templatesRes, twilioRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/sms/logs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/sms/templates`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/settings/twilio`, { withCredentials: true })
      ]);
      setDeals(dealsRes.data);
      setSmsLogs(logsRes.data);
      setTemplates(templatesRes.data);
      setTwilioConfigured(twilioRes.data.configured);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDealSelect = (dealId) => {
    const deal = deals.find(d => d.deal_id === dealId);
    if (deal && deal.owner_phone) {
      setSmsForm(prev => ({
        ...prev,
        deal_id: dealId,
        to_numbers: prev.to_numbers.includes(deal.owner_phone) 
          ? prev.to_numbers 
          : [...prev.to_numbers, deal.owner_phone]
      }));
    } else {
      setSmsForm(prev => ({ ...prev, deal_id: dealId }));
    }
  };

  const addManualNumber = () => {
    const cleaned = manualNumber.replace(/[^0-9+]/g, '');
    if (cleaned.length >= 10 && !smsForm.to_numbers.includes(cleaned)) {
      setSmsForm(prev => ({
        ...prev,
        to_numbers: [...prev.to_numbers, cleaned]
      }));
      setManualNumber("");
    } else {
      toast.error("Invalid or duplicate number");
    }
  };

  const removeNumber = (num) => {
    setSmsForm(prev => ({
      ...prev,
      to_numbers: prev.to_numbers.filter(n => n !== num)
    }));
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      let message = template.message;
      
      // Merge deal data if selected
      if (smsForm.deal_id) {
        const deal = deals.find(d => d.deal_id === smsForm.deal_id);
        if (deal) {
          message = message
            .replace(/{{address}}/g, deal.property_address || '')
            .replace(/{{price}}/g, deal.asking_price?.toLocaleString() || '')
            .replace(/{{owner}}/g, deal.owner_name || '');
        }
      }
      
      setSmsForm(prev => ({ ...prev, message }));
    }
  };

  const sendSMS = async () => {
    if (!twilioConfigured) {
      toast.error("Please configure Twilio credentials in Settings first");
      return;
    }
    if (smsForm.to_numbers.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!smsForm.message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/sms/send`,
        smsForm,
        { withCredentials: true }
      );
      
      toast.success(`Sent ${response.data.sent} message(s)`);
      setSmsForm({ to_numbers: [], message: "", deal_id: "" });
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.message) {
      toast.error("Name and message required");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/sms/templates`,
        templateForm,
        { withCredentials: true }
      );
      toast.success("Template saved");
      setShowTemplateModal(false);
      setTemplateForm({ name: "", message: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  // Feature gate removed
  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className="ml-64 flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase">SMS Messaging</h1>
            <p className="text-white/70 mt-1">Send text messages to property owners and leads</p>
          </div>
          {!twilioConfigured && (
            <Link to="/settings">
              <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/10 rounded-none">
                <Settings className="w-4 h-4 mr-2" />Configure Twilio
              </Button>
            </Link>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Compose SMS */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <h2 className="text-white font-bold mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-gold" />Compose Message
            </h2>

            {/* Deal Selection */}
            <div className="space-y-2 mb-4">
              <Label className="text-white/70">Link to Deal (optional)</Label>
              <Select onValueChange={handleDealSelect}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white">
                  <SelectValue placeholder="Select a deal..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                  {deals.filter(d => d.owner_phone).map(deal => (
                    <SelectItem key={deal.deal_id} value={deal.deal_id} className="text-white hover:bg-white/[0.06]">
                      {deal.property_address} - {deal.owner_phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipients */}
            <div className="space-y-2 mb-4">
              <Label className="text-white/70">Recipients</Label>
              <div className="flex gap-2">
                <Input
                  value={manualNumber}
                  onChange={(e) => setManualNumber(e.target.value)}
                  placeholder="+1 555-123-4567"
                  className="bg-black border-white/[0.08] text-white"
                />
                <Button onClick={addManualNumber} variant="outline" className="border-white/[0.1] text-white hover:bg-white/[0.06] rounded-none">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {smsForm.to_numbers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {smsForm.to_numbers.map(num => (
                    <span key={num} className="bg-black px-3 py-1 text-sm text-white flex items-center gap-2">
                      <Phone className="w-3 h-3 text-gold" />
                      {num}
                      <button onClick={() => removeNumber(num)} className="text-white/70 hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Template Selection */}
            {templates.length > 0 && (
              <div className="space-y-2 mb-4">
                <Label className="text-white/70">Use Template</Label>
                <div className="flex gap-2">
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="bg-black border-white/[0.08] text-white">
                      <SelectValue placeholder="Select template..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      {templates.map(tpl => (
                        <SelectItem key={tpl.template_id} value={tpl.template_id} className="text-white hover:bg-white/[0.06]">
                          {tpl.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowTemplateModal(true)} variant="outline" className="border-white/[0.1] text-white hover:bg-white/[0.06] rounded-none">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Message */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <Label className="text-white/70">Message</Label>
                <span className="text-xs text-white/70">{smsForm.message.length}/160</span>
              </div>
              <Textarea
                value={smsForm.message}
                onChange={(e) => setSmsForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Hi {{owner}}, I'm interested in your property at {{address}}..."
                className="bg-black border-white/[0.08] text-white min-h-32"
              />
              <p className="text-xs text-white/70">Use {"{{address}}"}, {"{{price}}"}, {"{{owner}}"} to merge deal data</p>
            </div>

            <Button 
              onClick={sendSMS} 
              disabled={sending || !twilioConfigured || smsForm.to_numbers.length === 0}
              className="w-full gold-gradient-btn rounded-none font-bold h-12"
            >
              {sending ? "Sending..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {smsForm.to_numbers.length} Recipient(s)
                </>
              )}
            </Button>

            {!twilioConfigured && (
              <p className="text-amber-500 text-sm text-center mt-3">
                ⚠️ Twilio not configured. Go to Settings to add your credentials.
              </p>
            )}
          </div>

          {/* SMS History */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <h2 className="text-white font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gold" />Recent Messages
            </h2>

            {smsLogs.length === 0 ? (
              <div className="text-center py-12 text-white/70">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No messages sent yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {smsLogs.map(log => (
                  <div key={log.sms_id} className="bg-black p-3 border border-white/[0.08]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gold" />
                        {log.to_number}
                      </span>
                      {log.status === "sent" ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-white/70 text-sm line-clamp-2">{log.message}</p>
                    <p className="text-white/70 text-xs mt-2">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Save Template Modal */}
        <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
            <DialogHeader>
              <DialogTitle>Save SMS Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Template Name</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-black border-white/[0.08] text-white"
                  placeholder="e.g., Initial Outreach"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Message</Label>
                <Textarea
                  value={templateForm.message}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, message: e.target.value }))}
                  className="bg-black border-white/[0.08] text-white min-h-24"
                  placeholder="Hi {{owner}}, I noticed your property at {{address}}..."
                />
              </div>
              <Button onClick={saveTemplate} className="w-full gold-gradient-btn rounded-none font-bold">
                <Save className="w-4 h-4 mr-2" />Save Template
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
